/**
 * useSubscription Hook
 *
 * Centralized subscription capability checks.
 * Derives all plan-gated capabilities from `user.subscriptionTier`
 * so every component uses consistent logic.
 */

import { useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { normalizePlanId, type PublicPlanId } from '../services/subscriptionPlans.service'

export interface SubscriptionCapabilities {
  /** Normalized tier id */
  tier: PublicPlanId
  /** Display name for the current tier */
  tierLabel: string
  /** True when on the free starter plan */
  isFreeTier: boolean
  /** Can add/edit/delete inventory, manage manual prices, record sales, etc. */
  canInventoryWrite: boolean
  /** Can use bulk operations: CSV import, batch sale/delete */
  canBulkOps: boolean
  /** Can access QR code generation (professional+) */
  canAccessQrCodes: boolean
  /** Can access Trade Center */
  canAccessTradeCenter: boolean
  /** Can access Social Planner calendar (basic+) */
  canAccessCalendar: boolean
  /** Can create and publish social posts (professional+); the calendar stays basic+ */
  canAccessSocialPosts: boolean
  /** Can run the in-store Store Display (professional+) */
  canAccessStoreDisplay: boolean
  /** Can use the card scanner's batch mode (professional+); single scans are open to every plan */
  canUseScannerBatch: boolean
  /** Can use the Centering Check (enterprise) */
  canAccessCentering: boolean
  /** Whether the current plan (or super-admin role) unlocks a capability */
  can: (capability: Capability) => boolean
  /** The minimum tier required for a given capability */
  requiredTierFor: (capability: Capability) => PublicPlanId
  /** Human-readable label for a tier */
  tierName: (tier: PublicPlanId) => string
}

export type Capability =
  | 'inventoryWrite'
  | 'bulkOps'
  | 'qrCodes'
  | 'tradeCenter'
  | 'calendar'
  | 'socialPosts'
  | 'storeDisplay'
  | 'scannerBatch'
  | 'centering'

const TIER_LABELS: Record<PublicPlanId, string> = {
  starter_free: 'Starter (Free)',
  basic: 'Basic',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

/** Minimum tier needed to unlock each capability */
const CAPABILITY_TIER: Record<Capability, PublicPlanId> = {
  inventoryWrite: 'basic',
  bulkOps: 'basic',
  qrCodes: 'professional',
  tradeCenter: 'basic',
  calendar: 'basic',
  socialPosts: 'professional',
  storeDisplay: 'professional',
  scannerBatch: 'professional',
  centering: 'enterprise',
}

const TIER_RANK: Record<PublicPlanId, number> = {
  starter_free: 0,
  basic: 1,
  professional: 2,
  enterprise: 3,
}

function hasTierAccess(userTier: PublicPlanId, requiredTier: PublicPlanId): boolean {
  return TIER_RANK[userTier] >= TIER_RANK[requiredTier]
}

export function useSubscription(): SubscriptionCapabilities {
  const { user } = useAuth()

  return useMemo(() => {
    const tier = normalizePlanId(user?.subscriptionTier ?? null)
    const isSuperAdmin = user?.role === 'super_admin'
    const status = String(user?.subscriptionStatus || '').toLowerCase()
    const endsAtTs = user?.subscriptionEndsAt ? new Date(user.subscriptionEndsAt).getTime() : Number.NaN
    const ended = Number.isFinite(endsAtTs) ? Date.now() > endsAtTs : true
    const shouldDowngradeToFree =
      (status === 'canceled' || status === 'cancelled' || status === 'unpaid') && ended
    const effectiveTier: PublicPlanId = shouldDowngradeToFree ? 'starter_free' : tier

    return {
      tier: effectiveTier,
      tierLabel: TIER_LABELS[effectiveTier],
      isFreeTier: effectiveTier === 'starter_free',

      canInventoryWrite: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.inventoryWrite),
      canBulkOps: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.bulkOps),
      canAccessQrCodes: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.qrCodes),
      canAccessTradeCenter: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.tradeCenter),
      canAccessCalendar: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.calendar),
      canAccessSocialPosts: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.socialPosts),
      canAccessStoreDisplay: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.storeDisplay),
      canUseScannerBatch: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.scannerBatch),
      canAccessCentering: isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER.centering),

      can: (cap: Capability) => isSuperAdmin || hasTierAccess(effectiveTier, CAPABILITY_TIER[cap]),
      requiredTierFor: (cap: Capability) => CAPABILITY_TIER[cap],
      tierName: (t: PublicPlanId) => TIER_LABELS[t],
    }
  }, [user?.subscriptionTier, user?.subscriptionStatus, user?.subscriptionEndsAt, user?.role])
}
